/**
 * Centralized API Service with automatic JWT token handling
 */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import jwtService from './jwtService';
import { API_BASE_URL } from '../config/supabase';

class APIService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
    });

    // Request interceptor to add authorization header
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Try to get user token first, then admin token
        const userAuthHeader = jwtService.getUserAuthHeader();
        const adminAuthHeader = jwtService.getAdminAuthHeader();
        
        if (userAuthHeader) {
          config.headers.Authorization = userAuthHeader;
          console.log('🔐 Adding user authorization header to API call:', config.url);
        } else if (adminAuthHeader) {
          config.headers.Authorization = adminAuthHeader;
          console.log('🔐 Adding admin authorization header to API call:', config.url);
        } else {
          console.log('⚠️ No authorization token available for API call:', config.url);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token expiration
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          console.log('🔐 Unauthorized access - clearing tokens');
          jwtService.clearUserToken();
          jwtService.clearAdminToken();
          // Optionally redirect to login page
          // window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // GET request
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get(url, config);
    return response.data;
  }

  // POST request
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post(url, data, config);
    return response.data;
  }

  // PUT request
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.put(url, data, config);
    return response.data;
  }

  // DELETE request
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete(url, config);
    return response.data;
  }

  // PATCH request
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.patch(url, data, config);
    return response.data;
  }

  // Get the underlying axios instance for advanced usage
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Create and export a singleton instance
const apiService = new APIService();
export default apiService;
